<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-clipboard-text-clock"></span></div>
      <div>
        <div class="id-h1">{{ $t('courseHistory.title') }}</div>
        <div class="pg-sub">{{ $t('courseHistory.ourCourses') }}</div>
      </div>
    </div>

    <!-- แท็บเฉพาะผู้จัดการ (head): คอร์สที่จบแล้ว / คอร์สที่ถูกลบ — role อื่นเห็นหน้าเดิม (ไม่มีแท็บ)
         สไตล์เดียวกับ ClosureManagement · ตาราง/ทูลบาร์ใช้ชุดเดียว สลับ endpoint ตาม tab -->
    <div class="cchf-tabs" v-if="isHead">
      <button type="button" class="id-btn" :class="tab === 'finished' ? 'id-btn-primary' : 'id-btn-ghost'" @click="switchTab('finished')">
        <span class="mdi mdi-book-check-outline"></span> {{ $t('courseHistory.tabFinished') }}
      </button>
      <button type="button" class="id-btn" :class="tab === 'deleted' ? 'id-btn-primary' : 'id-btn-ghost'" @click="switchTab('deleted')">
        <span class="mdi mdi-delete-clock-outline"></span> {{ $t('courseHistory.tabDeleted') }}
      </button>
    </div>

    <!-- server-side search + pagination preserved (getCustomerCourseList / tableOptions / totalItems / search watcher) -->
    <div class="grid-toolbar">
      <label class="grid-search"><span class="mdi mdi-magnify"></span>
        <input class="id-input" v-model="search" :placeholder="$t('btn.search')" /></label>
      <id-select class="grid-filter" v-model="filterCoursetype" :placeholder="$t('customerCourse.allTypes')" @update:model-value="applyFilter"
        :options="[{ value: '', label: $t('customerCourse.allTypes') }, { value: 'Monthly', label: 'Monthly' }, { value: 'Limited', label: 'Limited' }]"></id-select>
      <!-- ตัวกรองการชำระเงิน — ชุดเดียวกับหน้า "คอร์สของลูกค้า" (ตารางนี้โชว์คอลัมน์การชำระอยู่แล้ว
           คอร์สที่จบไปแล้วแต่ยังค้างจ่ายมีจริง จึงต้องกรองหาได้เหมือนกัน) -->
      <id-select class="grid-filter" v-model="filterPaid" :placeholder="$t('customerCourse.allPayments')" @update:model-value="applyFilter"
        :options="[{ value: '', label: $t('customerCourse.allPayments') }, { value: '1', label: $t('customerCourse.paid') }, { value: '0', label: $t('customerCourse.unpaid') }]"></id-select>
      <id-select class="grid-filter" v-model="filterPromotion" :placeholder="$t('customerCourse.allPromotions')" @update:model-value="applyFilter"
        :options="[{ value: '', label: $t('customerCourse.allPromotions') }, { value: '1', label: $t('customerCourse.promotionBadge') }, { value: '0', label: $t('customerCourse.notPromo') }]"></id-select>
      <span class="grid-spacer"></span>
      <button class="id-btn id-btn-sm" @click="initialize"><span class="mdi mdi-refresh"></span> {{ $t('btn.refresh') }}</button>
    </div>
    <div class="scard" style="padding:14px">
      <div style="overflow-x:auto">
        <table class="idt">
          <thead>
            <tr>
              <th v-for="h in headers" :key="h.key"
                :class="{ 'idt-action': ['view', 'edit', 'delete', 'finish', 'restore', 'unfinish'].includes(h.key), 'idt-sortable': h.sortable !== false, 'idt-sorted': $sortDir(tableOptions.sortBy, h.key) }"
                :style="{ textAlign: (h.align === 'center' || h.align === 'end') ? 'center' : 'left' }" @click="applySort(h)">
                {{ h.title }}
                <span v-if="$sortDir(tableOptions.sortBy, h.key)" class="mdi idt-sort-ico" :class="$sortDir(tableOptions.sortBy, h.key) === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up'"></span>
              </th>
            </tr>
          </thead>
          <tbody v-if="loadingCustomerCourse" class="id-fade-in" key="sk">
            <tr v-for="i in 6" :key="'sk' + i">
              <td v-for="h in headers" :key="h.key"><div class="id-skel" style="height:18px"></div></td>
            </tr>
          </tbody>
          <tbody v-else class="id-fade-in" :key="tableOptions.page">
            <tr v-for="item in courselist" :key="item.courserefer">
              <td v-for="h in headers" :key="h.key" :class="{ 'idt-action': ['view', 'edit', 'delete', 'finish', 'restore', 'unfinish'].includes(h.key) }" :style="{ textAlign: (h.align === 'center' || h.align === 'end') ? 'center' : 'left' }">
                <template v-if="h.key === 'courserefer'"><span class="id-copy">{{ item.courserefer }}<span class="mdi mdi-content-copy id-copy-btn" v-hint="$t('btn.copy')" @click="copyToClipboard(item.courserefer)"></span></span></template>
                <template v-else-if="h.key === 'startdate'">{{ format_date(item.startdate) }}</template>
                <template v-else-if="h.key === 'expiredate'"><span :style="{ color: (item.expiredate != null && isExpiredDate(item.expiredate)) ? 'var(--c-error)' : '' }">{{ expireDateLeft(item.expiredate) }}</span></template>
                <template v-else-if="h.key === 'remaining'">
                  <span v-if="item.coursetype === 'Monthly'" class="badge badge-primary">{{ $t('home.monthly') }}</span>
                  <span v-else-if="item.remaining !== null && item.remaining !== ''" class="badge badge-primary">{{ item.remaining }} {{ $t('common.times') }}</span>
                  <span v-else>-</span>
                </template>
                <template v-else-if="h.key === 'paid'">
                  <span v-if="item.paid == 1" class="badge badge-success">{{ $t('customerCourse.paid') }}</span>
                  <span v-else class="badge badge-error">{{ $t('customerCourse.unpaid') }}</span>
                </template>
                <!-- แท็บคอร์สที่ถูกลบ: ใครลบ + เมื่อไหร่ (editdate ของแถว history = เวลาที่กดลบ) -->
                <template v-else-if="h.key === 'deleteby'">{{ item.deleteby || '-' }}</template>
                <template v-else-if="h.key === 'editdate'">{{ format_datetime(item.editdate) }}</template>
                <!-- เจ้าของเคาะ 18 ส.ค.: กลับมาใช้ลูกตา + หัวคอลัมน์ "ดู" (เคย mdi-history ช่วง 17 ส.ค.) -->
                <template v-else-if="h.key === 'view'"><span class="mdi mdi-eye" style="color:var(--c-info);cursor:pointer;font-size:20px" @click="editItem(item)"></span></template>
                <!-- ปุ่มกู้คืน (undo) — เฉพาะแท็บคอร์สที่ถูกลบ (PR-2 lite, เจ้าของเคาะ 2026-09-08) -->
                <template v-else-if="h.key === 'restore'"><span class="mdi mdi-restore" v-hint="$t('courseHistory.restoreBtn')" :aria-label="$t('courseHistory.restoreBtn')" style="color:var(--c-success);cursor:pointer;font-size:20px" @click="restoreItem(item)"></span></template>
                <!-- ปุ่มยกเลิกการจบคอร์ส (undo) — เฉพาะแท็บคอร์สที่จบแล้ว (เจ้าของเคาะ 2026-09-10) -->
                <template v-else-if="h.key === 'unfinish'"><span class="mdi mdi-undo-variant" v-hint="$t('courseHistory.unfinishBtn')" :aria-label="$t('courseHistory.unfinishBtn')" style="color:var(--c-success);cursor:pointer;font-size:20px" @click="unfinishItem(item)"></span></template>
                <template v-else>{{ item[h.key] }}</template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="!loadingCustomerCourse && courselist.length === 0" class="grid-empty"><span class="mdi mdi-book-off-outline"></span>{{ isDeletedTab ? $t('courseHistory.noDeletedList') : $t('customerCourse.noCourseList') }}</div>
      <id-grid-footer v-if="totalItems > 0"
        :page="tableOptions.page"
        :page-count="Math.max(1, Math.ceil(totalItems / tableOptions.itemsPerPage))"
        :per-page="tableOptions.itemsPerPage" :total="totalItems"
        @update:page="(p) => { tableOptions.page = p; getCustomerCourseList(true); }"
        @update:per-page="(n) => { tableOptions.itemsPerPage = n; tableOptions.page = 1; getCustomerCourseList(true); }" />
    </div>

    <id-modal v-model="dialog" size="xl" icon="mdi-book-check-outline" :title="formTitle">
      <div class="modal-sec"><span class="mdi mdi-book-open-variant"></span> {{ $t('courseHistory.courseInfo') }}</div>
      <div class="t-cap" style="margin-bottom:4px">{{ $t('table.courseRefer') }} : <b>{{ editedItem.courserefer }}</b></div>
      <!-- แทนบรรทัด course_user เดิม — แยก "ถืออยู่ตอนนี้" กับ "เคยใช้" (CourseHoldersLine.vue) -->
      <course-holders-line :holders="courseHolders" :ever-used="courseEverUsed" />
      <div class="t-cap" v-if="editedIndex > -1" style="margin-bottom:12px">
        {{ $t('table.createBy') }}: <b>{{ editedItem.createby || '-' }}</b> · {{ format_datetime(editedItem.createdate) || '-' }}
        <!-- ⚠️ แถวในตารางประวัติ (แท็บคอร์สที่ถูกลบ) มี editdate = "เวลาที่ลบ" ไม่ใช่เวลาแก้ไขล่าสุด
             (deleteCustomerCourse ประทับ deleteby ก่อน copy → ON UPDATE bump คอลัมน์นี้)
             จึงห้ามเอาไปแปะคู่กับ updateby ในแท็บนั้น ไม่งั้นอ่านเป็น "แก้ไขโดย X เมื่อ <เวลาที่ลบ>" -->
        <template v-if="editedItem.updateby">&nbsp;·&nbsp; {{ $t('table.updateBy') }}: <b>{{ editedItem.updateby }}</b><template v-if="!isDeletedTab"> · {{ format_datetime(editedItem.editdate) || '-' }}</template></template>
        <template v-if="editedItem.deleteby">&nbsp;·&nbsp; {{ $t('table.deleteBy') }}: <b>{{ editedItem.deleteby }}</b><template v-if="isDeletedTab"> · {{ format_datetime(editedItem.editdate) || '-' }}</template></template>
      </div>
      <div class="form-grid-3">
        <div class="field"><label>{{ $t('table.courseName') }}</label>
          <input class="id-input" :value="editedItem.coursename || (editedItem.course && editedItem.course.coursename)" disabled></div>
        <div class="field"><label>{{ $t('table.courseType') }}</label><input class="id-input" :value="editedItem.coursetype" disabled></div>
        <div class="field"><label>{{ $t('customerCourse.period') }}</label><input class="id-input" :value="editedItem.period" disabled></div>
      </div>
      <div class="form-grid-3" style="margin-top:14px">
        <div class="field"><label>{{ $t('table.remaining') }}</label><input class="id-input" :value="editedItem.remaining" disabled></div>
        <div class="field"><label>{{ $t('table.startDate') }}</label><input class="id-input" :value="editedItem.startdate" disabled></div>
        <div class="field"><label>{{ $t('table.expireDate') }}</label><input class="id-input" :value="editedItem.expiredate" disabled></div>
      </div>

      <!-- ส่วนการชำระเงิน — ต้องมีครบเท่าหน้าสร้างคอร์ส (ราคา · วันที่จ่าย · วิธีจ่าย · ค่าธรรมเนียม)
           เดิมขาดสามช่องเงินไป ทำให้กดดูประวัติแล้วไม่รู้ว่าคอร์สนั้นขายไปเท่าไหร่ ทั้งที่เป็นข้อมูล
           ที่ต้องใช้บ่อยที่สุดเวลาย้อนดู · อ่านอย่างเดียวทั้งหมดเหมือนช่องอื่นในหน้านี้ -->
      <div class="modal-sec mt"><span class="mdi mdi-cash-multiple"></span> {{ $t('table.payment') }}</div>
      <div class="form-grid-3" style="align-items:end">
        <label class="id-cbx" style="height:42px">
          <span class="id-check" :class="{ on: editedItem.paid == 1 }"><span class="mdi mdi-check"></span></span>
          {{ editedItem.paid ? $t('customerCourse.paid') : $t('customerCourse.unpaid') }}
        </label>
        <div class="field"><label>{{ $t('table.price') }}</label>
          <input class="id-input" :value="editedItem.price === null || editedItem.price === undefined || editedItem.price === '' ? '-' : $money(editedItem.price)" disabled></div>
        <div class="field"><label>{{ $t('customerCourse.payDate') }}</label><input class="id-input" :value="editedItem.paydate || '-'" disabled></div>
      </div>
      <div class="form-grid-3" style="align-items:end;margin-top:14px">
        <div class="field"><label>{{ $t('customerCourse.paymentMethod') }}</label>
          <input class="id-input" :value="paymentMethodLabel(editedItem.paymentmethod)" disabled></div>
        <!-- NULL = แถวเก่าที่ยังไม่เคยเก็บค่าธรรมเนียม ต่างจาก 0.00 ที่แปลว่า "ยืนยันแล้วว่าไม่มี"
             จึงต้องแสดงเป็น '-' ห้ามตีเป็น 0.00 (กติกาเดียวกับที่รายงานการเงินใช้แยกสองกรณีนี้) -->
        <div class="field"><label>{{ $t('customerCourse.paymentFee') }}</label>
          <input class="id-input" :value="editedItem.payment_fee === null || editedItem.payment_fee === undefined || editedItem.payment_fee === '' ? '-' : $money(editedItem.payment_fee)" disabled></div>
      </div>
      <!-- multi-slip: จอนี้ดูอย่างเดียว (canAdd/canDelete = false) แต่ได้ผลตรวจรายใบ + สถานะยอดรวม
           มาด้วย ซึ่งเดิมหน้าประวัติคอร์สไม่เคยมีเลย -->
      <div class="field full" style="margin-top:12px">
        <slip-list-panel v-if="editedIndex > -1" context="course" :refer="editedItem.courserefer"
          :preloaded="slipPreload"
          :can-add="false" :can-delete="false" :can-reverify="false" :payment-method="editedItem.paymentmethod"
          @error="msg => $emit('onErrorHandler', msg)"></slip-list-panel>
      </div>
      <div class="field full" style="margin-top:14px"><label>{{ $t('customerCourse.shortNote') }}</label>
        <textarea class="id-input id-textarea" :value="editedItem.shortnote" readonly></textarea></div>

      <template v-if="editedIndex > -1">
        <div class="modal-sec mt"><span class="mdi mdi-history"></span> {{ $t('table.courseUsageHistory') }}</div>
        <div class="id-modal-grid"><div class="id-modal-grid-scroll" style="max-height:30vh">
          <v-data-table :headers="CourseUsingHeaders" :items="CourseUsingtList" density="compact" :items-per-page="1000">
            <template v-slot:item.index="{ item }">{{ CourseUsingtList.indexOf(item) + 1 }}</template>
            <template v-slot:item.classdate="{ item }"><p>{{ format_date(item.classdate) }} </p></template>
            <template v-slot:item.createby="{ item }"><booked-by-cell :name="item.createby" :role="item.createby_role" :at="item.createdate" /></template>
            <template v-slot:item.lastedit_by="{ item }"><booked-by-cell :name="item.lastedit_by" :role="item.lastedit_role" :at="item.lastedit_at" /></template>
            <template #bottom></template>
          </v-data-table>
        </div></div>
        <!-- ไทม์ไลน์การแก้ไขจาก taction_log — ประวัติคอร์สใช้ courserefer เดิม จึงเห็น log
             ชุดเดียวกับตอนที่ใบยังอยู่หน้าคอร์สของลูกค้า -->
        <edit-history-timeline v-if="dialog" kind="customercourse" :refid="editedItem.courserefer"
          :preloaded="historyPreload"></edit-history-timeline>
        <course-usage-timeline v-if="dialog" :refid="editedItem.courserefer"
          :preloaded="usagePreload"></course-usage-timeline>
      </template>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="close">{{ $t('btn.close') }}</button>
      </template>
    </id-modal>

    <!-- ยืนยันกู้คืนแพ็กเกจ (undo) — ไม่ใช่การกระทำทำลาย จึงใช้สีปกติ ไม่ใช้สีแดงเหมือน dialogDelete -->
    <id-modal v-model="dialogRestore" size="sm" icon="mdi-restore" :title="$t('courseHistory.confirmRestoreTitle')" persistent>
      <p style="margin:0">{{ $t('courseHistory.confirmRestore', { ref: restoreTarget && restoreTarget.courserefer }) }}</p>
      <p class="t-cap" style="margin-top:8px">{{ $t('courseHistory.confirmRestoreNote') }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="dialogRestore = false; restoreTarget = null">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="restoreItemConfirm"><span class="mdi mdi-restore"></span> {{ $t('btn.confirm') }}</button>
      </template>
    </id-modal>

    <!-- ยืนยันยกเลิกการจบคอร์ส (undo) -->
    <id-modal v-model="dialogUnfinish" size="sm" icon="mdi-undo-variant" :title="$t('courseHistory.confirmUnfinishTitle')" persistent>
      <p style="margin:0">{{ $t('courseHistory.confirmUnfinish', { ref: unfinishTarget && unfinishTarget.courserefer }) }}</p>
      <p class="t-cap" style="margin-top:8px">{{ $t('courseHistory.confirmUnfinishNote') }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="dialogUnfinish = false; unfinishTarget = null">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="unfinishItemConfirm"><span class="mdi mdi-undo-variant"></span> {{ $t('btn.confirm') }}</button>
      </template>
    </id-modal>

  </div>
</template>
<script>
import axios from "axios";
import { mapGetters } from "vuex";
import moment from "moment";
import { t } from "@/i18n";
import { formatDateTimeBKK } from "../../utils/datetime";
import { fetchSlipImages } from "@/utils/slips";
import { fetchEditHistory, fetchCourseUsage } from "@/utils/history";
export default {
  data: () => ({
    search: "",
    dialog: false,
    loadingCustomerCourse: false,
    slipPreload: null,   // รายการสลิปที่โหลดไว้ก่อนเปิด dialog (ส่งให้พาเนลผ่าน :preloaded)
    historyPreload: null,  // ไทม์ไลน์การแก้ไขที่โหลดไว้ก่อนเปิด dialog
    usagePreload: null,    // ไทม์ไลน์การใช้คอร์ส โหลดคู่กับ historyPreload
    editedIndex: -1,
    editedItem: {
      courserefer: null,
      course: null,
      coursename: null,
      coursetype: null,
      course_shortname: null,
      remaining: null,
      startdate: null,
      expiredate: null,
      period: null,
      paid: 0,
      paydate: null,
      slip_customer: '',
      slip_image_url: '',
      shortnote: '',
    },
    defaultItem: {
      courserefer: null,
      course: null,
      coursename: null,
      coursetype: null,
      course_shortname: null,
      remaining: null,
      startdate: null,
      expiredate: null,
      period: null,
      paid: 0,
      paydate: null,
      slip_customer: '',
      slip_image_url: '',
      shortnote: '',
    },
    showPreview: false,
    isViewMode: false, // เปลี่ยนเป็น false เมื่ออยู่ในโหมดแก้ไข
    CourseUsingHeadersData: [],
    CourseUsingtList: [],
    // ใครถือแพ็กเกจนี้อยู่ / เคยใช้ — จาก /getStudentCourseDetail (ดู CourseHoldersLine.vue)
    courseHolders: null,
    courseEverUsed: null,
    courselist: [],
    notNullRules: [
      v => !!v || t('common.required'),
    ],
    totalItems: 0,
    tableOptions: { page: 1, itemsPerPage: 10, sortBy: [] },
    filterCoursetype: '',   // server filter — backend /getFinishedCustomerCourseList must read `coursetype`
    filterPaid: '',         // server filter — backend must be called with allowPaidFilter: true
    filterPromotion: '',    // server filter — backend /getFinishedCustomerCourseList must read `is_promotion` (1/0)
    searchTimer: null,
    isMounted: false,
    tab: 'finished',        // 'finished' = คอร์สที่จบแล้ว (เดิม) · 'deleted' = คอร์สที่ถูกลบ (head เท่านั้น)
    fetchSeq: 0,            // กันผลที่มาช้าเขียนทับแท็บที่ผู้ใช้เปลี่ยนไปแล้ว (ดู getCustomerCourseList)
    dialogRestore: false,   // ยืนยันก่อนกู้คืนแพ็กเกจ (ปุ่ม undo, PR-2 lite)
    restoreTarget: null,
    dialogUnfinish: false,  // ยืนยันก่อนยกเลิกการจบคอร์ส (ปุ่ม undo)
    unfinishTarget: null,
  }),

  computed: {
    ...mapGetters({
      token: "getToken",
    }),
    formTitle() {
      return this.$t('courses.viewCourse')
    },
    // แท็บ "คอร์สที่ถูกลบ" เปิดให้ผู้จัดการ (usertype '0') เท่านั้น — backend บังคับซ้ำที่
    // /getDeletedCustomerCourseList (403 ถ้าไม่ใช่ head) ตัวนี้แค่ซ่อนแท็บให้ role อื่นเห็นหน้าเดิม
    isHead() {
      const u = this.$store.getters.getUserdata;
      return String(u && u.usertype) === '0';
    },
    isDeletedTab() {
      return this.isHead && this.tab === 'deleted';
    },
    CourseUsingHeaders() {
      return [
        // คอลัมน์ '#' ต้องมีคู่กับสลอต item.index ด้านบนเสมอ — ชุดเดียวกับ dialog หน้าคอร์สของลูกค้า
        // (ตกไปตอนแยกไฟล์ สลอตมีอยู่แต่ไม่มี header ให้แสดง เลขลำดับจึงไม่เคยขึ้น)
        { title: '#', key: 'index', width: '50px', sortable: false },
        { title: this.$t('table.name'), key: 'fullname' },
        { title: this.$t('table.date'), key: 'classdate' },
        { title: this.$t('table.time'), key: 'classtime' },
        { title: this.$t('courseUsage.bookedBy'), key: 'createby' },
        { title: this.$t('bookingMgmt.updateBy'), key: 'lastedit_by' },
      ]
    },
    headers() {
      // แท็บคอร์สที่ถูกลบเพิ่ม 2 คอลัมน์ท้าย (ลบโดย/วันที่ลบ) — คอลัมน์อื่นชุดเดียวกับแท็บเดิม
      // เพื่อให้สองแท็บอ่านเทียบกันได้ (กติกาเดียวกับคู่ คอร์สของลูกค้า ↔ ประวัติคอร์ส)
      const base = [
        { title: this.$t('table.courseRef'), align: 'start', key: 'courserefer' },
        { title: this.$t('table.courseName'), key: 'coursename' },
        { title: this.$t('table.courseType'), key: 'coursetype' },
        { title: this.$t('table.startDate'), key: 'startdate', align: 'center' },
        { title: this.$t('table.expireDate'), key: 'expiredate', align: 'left' },
        { title: this.$t('table.remaining'), key: 'remaining', align: 'end' },
        { title: this.$t('table.courseUser'), key: 'userlist', align: 'left', sortable: false },
        { title: this.$t('table.payment'), key: 'paid', align: 'center', sortable: false },
      ]
      if (this.isDeletedTab) {
        base.push({ title: this.$t('table.deleteBy'), key: 'deleteby', align: 'left', sortable: false })
        base.push({ title: this.$t('courseHistory.deleteDate'), key: 'editdate', align: 'left' })
      }
      base.push({ title: this.$t('table.view'), key: 'view', align: 'center', sortable: false })
      // ปุ่มกู้คืนอยู่ท้ายสุด เฉพาะแท็บคอร์สที่ถูกลบ (สอดคล้องกับ isDeletedTab = head เท่านั้น)
      if (this.isDeletedTab) {
        base.push({ title: this.$t('courseHistory.restoreBtn'), key: 'restore', align: 'center', sortable: false })
      }
      // ปุ่มยกเลิกการจบคอร์ส (undo) — เฉพาะแท็บ "คอร์สที่จบแล้ว" ไม่จำกัด head เหมือนปุ่มจบคอร์สเอง
      // (requireMenu ฝั่ง backend ก็ไม่ได้จำกัดแค่ head เหมือนกัน)
      if (this.tab === 'finished') {
        base.push({ title: this.$t('courseHistory.unfinishBtn'), key: 'unfinish', align: 'center', sortable: false })
      }
      return base
    },
  },

  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.tableOptions.page = 1;
        this.getCustomerCourseList();
      }, 500);
    },
    dialog(val) {
      val || this.close();
    },
  },

  async created() {
    try {
      const token = this.$store.getters.getToken;
      //console.log("token ", token);
      if (!token) {
        this.errorMsg = "Not found token, Please login...";
        this.errorDialog = true;
        this.$emit("onClickChangeState", "login");
        return;
      }

      await axios
        .post(
          this.baseURL + "/verifyToken",
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          //console.dir(response);
          if (response.data.success) {
            this.initialize();
          }
        })
        .catch((error) => {
          //console.error(error);
          this.$emit("onErrorHandler", error.response.data.message);
          this.$emit("onClickChangeState", "login");
        });
    } catch (error) {
      this.$emit("onErrorHandler", error.message);
    }
  },

  mounted() {
    this.isMounted = true;
  },

  methods: {
    // ใช้คำแปลชุดเดียวกับหน้าสร้างคอร์ส ไม่ตั้งชื่อใหม่ — ค่าว่างคือ "ไม่ระบุ" ซึ่งต่างจาก
    // ยังไม่จ่าย จึงแสดงเป็น '-' เฉย ๆ ไม่เดาแทนผู้ใช้
    paymentMethodLabel(v) {
      const map = {
        cash: this.$t('customerCourse.paymentCash'),
        transfer: this.$t('customerCourse.paymentTransfer'),
        credit: this.$t('customerCourse.paymentCredit'),
      };
      return map[v] || '-';
    },
    async initialize() {
      this.tableOptions.page = 1;
      this.$emit("onLoading", true);
      await this.getCustomerCourseList();
      await this.getCourseLookup();
      this.$emit("onLoading", false);
    },
    copyToClipboard(newcourse) {
      if (newcourse) {
        navigator.clipboard.writeText(newcourse)
          .then(() => {
            //consol.log('คัดลอกคอร์สใหม่เรียบร้อยแล้ว');
          })
          .catch(() => {
            //console.error('ไม่สามารถคัดลอกได้: ', err);
          });
      } else {
        //this.$emit("onErrorHandler", "ไม่มีคอร์สใหม่ที่จะคัดลอก");
      }
    },

    // เตรียมข้อมูลให้ครบก่อนเปิด dialog (กติกาเดียวกับหน้าคอร์สของลูกค้า)
    async editItem(item) {
      this.$emit("onLoading", true);
      try {
        await this.editItemPrepare(item);
      } finally {
        this.$emit("onLoading", false);
      }
      this.dialog = true;
    },
    async editItemPrepare(item) {
      await this.getCourseLookup();
      this.editedIndex = this.courselist.findIndex(course => course.courserefer === item.courserefer);
      this.editedItem = Object.assign({}, item);
      if(this.editedItem.startdate) this.editedItem.startdate = this.format_date(item.startdate);
      if(this.editedItem.expiredate) this.editedItem.expiredate = this.format_date(item.expiredate);
      let course = this.courseLookup.find(
        (course) => course.courseid == this.editedItem.courseid
      );
      if(this.editedItem.paydate) this.editedItem.paydate = this.format_date(this.editedItem.paydate);
      this.editedItem.course = course;
      // เติมผู้ใช้คอร์ส/ตารางประวัติ + รายการสลิป + ไทม์ไลน์การแก้ไข ให้ครบก่อนเปิด (ดึงคู่ขนาน)
      const [, slipData, history, usage] = await Promise.all([
        this.checkCourseUser(),
        fetchSlipImages(this.baseURL, this.$store.getters.getToken, 'course', item.courserefer),
        fetchEditHistory(this.baseURL, this.$store.getters.getToken, 'customercourse', item.courserefer),
        fetchCourseUsage(this.baseURL, this.$store.getters.getToken, item.courserefer),
      ]);
      this.slipPreload = slipData;
      this.historyPreload = history;
      this.usagePreload = usage;
      if(this.editedItem.slip_image_url) {
        this.isViewMode = true;
      }
    },
    async checkCourseUser() {
      this.loadingCourse = true;
      // ล้างก่อนยิงเสมอ — ฟังก์ชันนี้กลืน error ของตัวเอง (dialog ยังเปิดต่อ) ถ้าไม่ล้าง
      // ตารางการใช้งานของ "ใบก่อนหน้า" จะค้างอยู่ในใบใหม่ (กติกาเดียวกับ CustomerCourse.vue)
      this.CourseUsingtList = [];
      this.courseHolders = null;
      this.courseEverUsed = null;
      await axios
        .get(
          this.baseURL + "/getStudentCourseDetail/" + this.editedItem.courserefer,
          {
            headers: { Authorization: `Bearer ${this.token}` },
          }
        )
        .then((response) => {
          //console.dir(response);
          if (response.data.success) {
            //console.log("getStudentCourseDetail", response.data);
            // ตารางการใช้งานมาจาก tbooking_group จึงมีได้แม้แพ็กเกจถูกลบไปแล้ว — อ่านนอก if
            this.CourseUsingtList = response.data.courseDetail || [];
            // holders/everUsed เป็นคนละบล็อกกับ results — แพ็กเกจที่จบ/ถูกลบยังมี everUsed อยู่
            this.courseHolders = response.data.holders || null;
            this.courseEverUsed = response.data.everUsed || null;
          } else {
            this.$emit("onErrorHandler", this.$t("customerCourse.loadFail"));
          }
        })
        .catch((error) => {
          //console.log(error);
          if (error.response && error.response.status == 401) {
            this.$emit("onErrorHandler", error.response.data.message);
            this.$emit("onClickChangeState", "login");
          } else {
            this.$emit("onErrorHandler", error.message);
          }
        });
      this.loadingCourse = false;
    },
    close() {
      this.dialog = false;
      this.courseHolders = null;      // ผู้ใช้ของใบก่อนห้ามไปโผล่ในใบถัดไป (เดิมค่านี้อยู่ใน
      this.courseEverUsed = null;     // editedItem จึงถูกล้างฟรีตอน Object.assign defaultItem)
      this.isViewMode = false;
      this.slipPreload = null;   // ข้อมูลสลิปของใบก่อนห้ามไปโผล่ในใบถัดไป
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    async getCourseLookup() {
      const token = this.$store.getters.getToken;
      await axios
        .get(this.baseURL + "/courseLookup", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          //console.dir(response);
          if (response.data.success) {
            this.courseLookup = response.data.results;
          }
        })
        .catch((error) => {
          if (error.response?.status == 401) {
            this.$emit("onErrorHandler", error.response.data.message);
            this.$emit("onClickChangeState", "login");
          } else {
            this.$emit("onErrorHandler", error.message);
          }
        });
    },
    selectCourse() {
      //console.log("selectCourse");
      let course = this.courseLookup.find(
        (course) => course.courseid == this.editedItem.courseid
      );
      this.editedItem.course_shortname = course.course_shortname;
    },
    applyFilter() { this.tableOptions.page = 1; this.getCustomerCourseList(); },
    // สลับแท็บ = ล้างหน้า/การเรียง แล้วดึงใหม่ (คนละชุดข้อมูล คนละ endpoint) — ตัวกรอง/คำค้นคงไว้
    // เพราะสองแท็บมีคอลัมน์เดียวกัน คนมักหาใบเดิมข้ามแท็บ
    switchTab(tab) {
      if (this.tab === tab) return;
      this.tab = tab;
      this.tableOptions.page = 1;
      this.tableOptions.sortBy = [];
      // ล้างแถวทันทีที่สลับ — หัวตารางเปลี่ยนตามแท็บทันที ถ้าปล่อยแถวเก่าไว้ระหว่างรอ (หรือดึงไม่สำเร็จ)
      // ใบที่ยังอยู่จะถูกอ่านใต้คอลัมน์ "ลบโดย/วันที่ลบ" เหมือนเป็นใบที่ถูกลบ
      this.courselist = [];
      this.totalItems = 0;
      this.getCustomerCourseList();
    },
    // click a sortable header → cycle the server sortBy (none→asc→desc→none), reset to page 1, refetch.
    applySort(h) {
      if (h.sortable === false) return;
      this.tableOptions.sortBy = this.$toggleSort(this.tableOptions.sortBy, h.key);
      this.tableOptions.page = 1;
      this.getCustomerCourseList(true);
    },
    // animate = true only for grid page / per-page changes → ≥1s skeleton.
    // ⚠️ หัวตาราง (headers) ผูกกับแท็บ ส่วนแถวมาจาก API — สองอย่างนี้ต้องมาจากแท็บเดียวกันเสมอ
    // จึงต้อง (1) ทิ้งผลที่มาช้าหลังผู้ใช้สลับแท็บไปแล้ว และ (2) ล้างแถวเมื่อดึงไม่สำเร็จ
    // ไม่งั้นแถวของแท็บเดิมจะถูกอ่านเป็นข้อมูลอีกแท็บ (เช่นใบที่ยังอยู่ถูกอ่านว่าถูกลบ)
    async getCustomerCourseList(animate = false) {
      this.loadingCustomerCourse = true;
      const t0 = Date.now();
      const token = this.$store.getters.getToken;
      const { page, itemsPerPage, sortBy } = this.tableOptions;
      const seq = ++this.fetchSeq;
      const stale = () => seq !== this.fetchSeq;
      await axios
        .post(
          this.baseURL + (this.isDeletedTab ? "/getDeletedCustomerCourseList" : "/getFinishedCustomerCourseList"),
          { page, itemsPerPage, search: this.search, sortBy, coursetype: this.filterCoursetype, paid: this.filterPaid, is_promotion: this.filterPromotion },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then(async (response) => {
          if (stale()) return;              // ผู้ใช้สลับแท็บ/กรองใหม่ระหว่างรอ — ผลนี้ของเก่าแล้ว
          if (response.data.success) {
            this.courselist = response.data.results ?? [];
            this.totalItems = response.data.total ?? 0;
          } else {
            this.courselist = [];
            this.totalItems = 0;
          }
          if (animate) await this.$minLoad(t0);
          this.loadingCustomerCourse = false;
        })
        .catch(async (error) => {
          if (stale()) return;
          this.courselist = [];
          this.totalItems = 0;
          if (animate) await this.$minLoad(t0);
          this.loadingCustomerCourse = false;
          if (error.response?.status == 401) {
            this.$emit("onErrorHandler", error.response.data.message);
            this.$emit("onClickChangeState", "customercourse");
          } else {
            // 403 = ไม่ใช่ผู้จัดการแต่ยิงแท็บคอร์สที่ถูกลบ — ข้อความจาก backend อ่านรู้เรื่องกว่า
            this.$emit("onErrorHandler", error.response?.data?.message || error.message);
          }
        });
    },
    onTableOptions({ page, itemsPerPage, sortBy }) {
      this.tableOptions = { ...this.tableOptions, page, itemsPerPage, sortBy };
      if (!this.isMounted) return;
      this.getCustomerCourseList();
    },
    onChangeStartDate() {
      //console.log("onChangeStartDate");
      this.editedItem.expiredate = null;
    },
    SQLDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    format_date(value) {
      if (value) {
        return moment(value).format("DD/MM/YYYY");
      }
    },
    // แสดง createdate/editdate (TIMESTAMP จาก backend) เป็นเวลาไทยคงที่ ไม่ขึ้นกับ timezone ของเครื่องผู้ใช้
    format_datetime(value) {
      return formatDateTimeBKK(value, "DD/MM/YYYY HH:mm");
    },
    expireDateLeft(value) {
        let result = "";
        if (value) {
            let remainingText = this.calExpireText(value);
            result = this.format_date(value) + " (" + remainingText + ")";
        }
        return result;
    },
    isExpiredDate(expdate) {
        if (!expdate) return false;
        return new Date(expdate) < new Date();
    },
    calExpireText(expdate) {
      if (!expdate) return '';
      const today = new Date();
      const expirationDate = new Date(expdate);
      let returnText = '';

      if (expirationDate < today) {
          returnText = this.$t('table.expired');
      } else {
          let years = expirationDate.getFullYear() - today.getFullYear();
          let months = expirationDate.getMonth() - today.getMonth();
          let days = expirationDate.getDate() - today.getDate();

          if (days < 0) {
              months -= 1;
              days += new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
          }
          if (months < 0) {
              years -= 1;
              months += 12;
          }

          if (years > 0) returnText += `${years} ${this.$t('table.yearAbbr')} `;
          if (months > 0) returnText += `${months} ${this.$t('table.monthAbbr')} `;
          if (days > 0) returnText += `${days} ${this.$t('table.dayAbbr')}`;
      }
      return returnText;
    },
    async showAddNewCustomerCourse() {
      await this.getCustomerCourseList();
      await this.getCourseLookup();
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.courseHolders = null;      // ผู้ใช้ของใบก่อนห้ามไปโผล่ในใบถัดไป
      this.courseEverUsed = null;
      this.dialog = true;
    },
    // ปุ่มกู้คืน (undo) แพ็กเกจที่ถูกลบ — เปิด confirm ก่อนเสมอ (ไม่ใช่การกระทำที่ทำลายข้อมูล
    // แต่ยังเป็นการเปลี่ยนสถานะ ควรให้กดยืนยันอีกครั้งกันมือลื่น)
    restoreItem(item) {
      this.restoreTarget = item;
      this.dialogRestore = true;
    },
    async restoreItemConfirm() {
      if (!this.restoreTarget) return;
      const courserefer = this.restoreTarget.courserefer;
      this.$emit("onLoading", true);
      try {
        const response = await axios.post(
          this.baseURL + "/restoreCustomerCourse",
          { courserefer },
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        if (response.data.success) {
          this.dialogRestore = false;
          this.restoreTarget = null;
          this.$emit("onSuccessHandler", response.data.message);
          await this.getCustomerCourseList();
        } else {
          this.$emit("onErrorHandler", response.data.message);
        }
      } catch (error) {
        if (error.response?.status == 401) {
          this.$emit("onErrorHandler", error.response.data.message);
          this.$emit("onClickChangeState", "login");
        } else {
          this.$emit("onErrorHandler", error.response?.data?.message || error.message);
        }
      } finally {
        this.$emit("onLoading", false);
      }
    },
    // ปุ่มยกเลิกการจบคอร์ส (undo) — เปิด confirm ก่อนเสมอ เหมือนปุ่มกู้คืนแพ็กเกจ
    unfinishItem(item) {
      this.unfinishTarget = item;
      this.dialogUnfinish = true;
    },
    async unfinishItemConfirm() {
      if (!this.unfinishTarget) return;
      const courserefer = this.unfinishTarget.courserefer;
      this.$emit("onLoading", true);
      try {
        const response = await axios.post(
          this.baseURL + "/unfinishCustomerCourse",
          { courserefer },
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        if (response.data.success) {
          this.dialogUnfinish = false;
          this.unfinishTarget = null;
          this.$emit("onSuccessHandler", response.data.message);
          await this.getCustomerCourseList();
        } else {
          this.$emit("onErrorHandler", response.data.message);
        }
      } catch (error) {
        if (error.response?.status == 401) {
          this.$emit("onErrorHandler", error.response.data.message);
          this.$emit("onClickChangeState", "login");
        } else {
          this.$emit("onErrorHandler", error.response?.data?.message || error.message);
        }
      } finally {
        this.$emit("onLoading", false);
      }
    },
  }
};
</script>
<style scoped>
/* แถบแท็บ — ชุดเดียวกับ ClosureManagement เพื่อให้หน้าที่มีแท็บทั้งระบบหน้าตาเหมือนกัน */
.cchf-tabs {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.highlighted-red {
  color: red;
}
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: lightgray;
}

.sticky-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background-color: lightgray;
}

.scrollable-content {
  max-height: 950px;
  overflow-y: auto;
}
</style>
